import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@app/constants/queryKeys';
import { useConfig } from '@app/config';
import { gql, request } from 'graphql-request';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { AUCTION_STATUS } from '@app/constants/nftMarketplace';
import { add, subDays } from 'date-fns';
import { useRewardNftsMarketplaceFiltersState } from '@app/state/rewardsNftsMarketplaceFiltersStore';
import { ApplicationConfig } from '@app/config/types';

async function fetcher(
  config: ApplicationConfig | undefined,
  sortOrder: string,
  auctionType: string,
  auctionStatus: string
) {
  if (!config) {
    return null;
  }

  const isNFTAvailableParam = auctionStatus !== AUCTION_STATUS.RECENTLY_SOLD;

  const endingSoonThreshold = Math.floor(
    add(new Date(), { days: 1 }).getTime() / 1000
  );
  const endingSoonParam =
    auctionStatus === AUCTION_STATUS.ENDING_SOON
      ? `end_lte: ${endingSoonThreshold}, end_gte: ${Math.floor(
          new Date().getTime() / 1000
        )},`
      : '';

  const recentlySoldThreshold = Math.floor(
    subDays(new Date(), 3).getTime() / 1000
  );
  const recentlySoldParam =
    auctionStatus === AUCTION_STATUS.RECENTLY_SOLD
      ? `end_gte: ${recentlySoldThreshold}, end_lte: ${Math.floor(
          new Date().getTime() / 1000
        )},`
      : '';

  const whitelistedNftsIds = config.NFT_MARKETPLACE.rewardNftsWhitelist
    .map(t => {
      return `"${t.toLowerCase()}"`;
    })
    .join(',');

  const res = await request<{ listings: NFTMarketplaceListing[] }>(
    `${config.URLS.subgraphUrlNftMarketplace}`,
    gql`
      query {
        listings (
          orderBy: price,
           orderDirection: ${sortOrder},
           where: {
              nftContractAddress_in: [${whitelistedNftsIds}]
              isNFTAvailable: ${isNFTAvailableParam},
              ${auctionType ? `type: "${auctionType}"` : ''},
              ${endingSoonParam},
              ${recentlySoldParam}, 
            }
        ) {
          id
          type
          maker
          currency
          price
          tokenId
          reserve
          nftContractAddress
          amount
          start
          end
          isNFTAvailable
          nftPool {
            id
            lpToken {
              symbol
              name
              decimals
              id
            }
          }
          nftMetadata {
            isPremium
            id
          }
        }
      }
    `
  );

  return res.listings;
}

export function useRewardNftsMarketplaceListings() {
  const config = useConfig();
  const { sortOrder, auctionType, auctionStatus } =
    useRewardNftsMarketplaceFiltersState();

  return useQuery<NFTMarketplaceListing[] | null>(
    [
      QueryKeys.GET_REWARD_NFTS_MARKETPLACE_LISTINGS,
      sortOrder,
      auctionType,
      auctionStatus,
      config?.URLS.subgraphUrlNftMarketplace
    ],
    () => fetcher(config, sortOrder, auctionType, auctionStatus)
  );
}
